import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { X, Home as HomeIcon, CheckCircle2 } from 'lucide-react';
import { createLead } from '../../lib/api';
import { formatIndianMobileE164, isValidIndianMobile, normalizeIndianPhone, phoneInputDigits } from '../../lib/phone';
import { markProjectCallbackRequested } from '../../lib/enquiryProjects';
import { trackEnquiryEvent } from './analytics';

const BRAND_GREEN = '#1B4332';

const NAME_RE = /.{2,}/;

function isDuplicateProjectError(err) {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'object' && detail?.code === 'DUPLICATE_PROJECT_CALLBACK') return true;
    if (typeof detail === 'string' && detail.toLowerCase().includes('another project')) return true;
    return false;
}

function validateField(name, value) {
    if (name === 'name') {
        if (!value || !value.trim()) return 'Please enter your name';
        if (!NAME_RE.test(value.trim())) return 'Name must be at least 2 characters';
        return '';
    }
    if (name === 'phone') {
        const cleaned = normalizeIndianPhone(value);
        if (!cleaned) return 'Please enter your phone number';
        if (!isValidIndianMobile(cleaned)) return 'Enter a valid 10-digit Indian mobile';
        return '';
    }
    return '';
}

/**
 * Reusable EnquiryPopup modal.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   contextTitle: string
 *   contextSubtitle?: string
 *   source: string
 *   triggerType?: string  (e.g. 'scroll_depth' | 'exit_intent' | 'manual')
 *   prefillProject?: string
 *   prefillCity?: string
 *   projectKey?: string
 *   propertyId?: string
 *   onDuplicateProject?: () => void
 */
export default function EnquiryPopup({
    open,
    onClose,
    contextTitle = 'Talk to a property expert',
    contextSubtitle = '',
    source = 'website',
    triggerType = 'manual',
    prefillProject = '',
    prefillCity = '',
    projectKey = '',
    propertyId = '',
    onDuplicateProject,
}) {
    const titleId = useId();
    const dialogRef = useRef(null);
    const firstFieldRef = useRef(null);
    const closeBtnRef = useRef(null);
    const openedAtRef = useRef(0);
    const startedRef = useRef(false);

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [errors, setErrors] = useState({ name: '', phone: '' });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    // Reset state each time popup opens
    useEffect(() => {
        if (!open) return undefined;
        setName('');
        setPhone('');
        setErrors({ name: '', phone: '' });
        setSubmitting(false);
        setSuccess(false);
        startedRef.current = false;
        openedAtRef.current = Date.now();
        trackEnquiryEvent('enquiry_popup_shown', { source, trigger_type: triggerType });
        return undefined;
    }, [open, source, triggerType]);

    // Lock body scroll while open + focus first input
    useEffect(() => {
        if (!open) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        // Move focus to first field after a tick (after render)
        const t = setTimeout(() => {
            if (firstFieldRef.current) firstFieldRef.current.focus();
            else if (closeBtnRef.current) closeBtnRef.current.focus();
        }, 30);
        return () => {
            clearTimeout(t);
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    const handleClose = useCallback(() => {
        const seconds = Math.round((Date.now() - openedAtRef.current) / 1000);
        trackEnquiryEvent('enquiry_popup_closed', { source, time_spent_seconds: seconds, trigger_type: triggerType });
        if (onClose) onClose();
    }, [onClose, source, triggerType]);

    // Escape to close + focus trap
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                handleClose();
                return;
            }
            if (e.key === 'Tab' && dialogRef.current) {
                const focusables = dialogRef.current.querySelectorAll(
                    'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
                );
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, handleClose]);

    const onFieldChange = (field) => (e) => {
        const v = e.target.value;
        if (!startedRef.current) {
            startedRef.current = true;
            trackEnquiryEvent('enquiry_form_started', { source, trigger_type: triggerType });
        }
        if (field === 'name') setName(v);
        if (field === 'phone') setPhone(phoneInputDigits(v));
        // Clear error while typing (validate on blur per spec)
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const onFieldBlur = (field) => () => {
        const value = field === 'name' ? name : phone;
        const msg = validateField(field, value);
        setErrors((prev) => ({ ...prev, [field]: msg }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nameErr = validateField('name', name);
        const phoneErr = validateField('phone', phone);
        setErrors({ name: nameErr, phone: phoneErr });
        if (nameErr || phoneErr) {
            // Focus the first invalid field
            if (nameErr && firstFieldRef.current) firstFieldRef.current.focus();
            return;
        }
        setSubmitting(true);
        const normalizedPhone = normalizeIndianPhone(phone);
        try {
            await createLead({
                name: name.trim(),
                phone: formatIndianMobileE164(normalizedPhone),
                email: '',
                message: prefillProject ? `Interested in: ${prefillProject}` : '',
                property_id: propertyId || undefined,
                property_title: prefillProject || contextSubtitle || '',
                city: prefillCity || '',
                project_key: projectKey || '',
                society_name: prefillProject || '',
                lead_type: 'BUY',
                source: (source || 'website').toUpperCase(),
            });
            setSuccess(true);
            if (projectKey) markProjectCallbackRequested(projectKey, { phone: normalizedPhone });
            trackEnquiryEvent('enquiry_form_submitted', {
                source,
                trigger_type: triggerType,
                has_email: false,
                city: prefillCity || '',
                project_key: projectKey || '',
            });
            setTimeout(() => {
                if (onClose) onClose();
            }, 4000);
        } catch (err) {
            if (isDuplicateProjectError(err)) {
                if (projectKey) markProjectCallbackRequested(projectKey, { phone: normalizedPhone });
                if (onClose) onClose();
                if (onDuplicateProject) onDuplicateProject();
            } else if (err?.response?.status === 429) {
                const detail = err?.response?.data?.detail;
                setErrors((prev) => ({
                    ...prev,
                    name: '',
                    phone: typeof detail === 'string' ? detail : 'Too many requests. Try again later.',
                }));
            } else {
                setErrors((prev) => ({ ...prev, name: '', phone: 'Could not submit. Please try again.' }));
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
            data-testid="enquiry-popup"
            data-source={source}
            data-trigger={triggerType}
        >
            {/* Backdrop */}
            <button
                type="button"
                aria-label="Close enquiry form"
                onClick={handleClose}
                className="absolute inset-0 bg-black/55 transition-opacity"
                tabIndex={-1}
            />

            {/* Modal */}
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative w-full sm:max-w-[480px] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-[enquirySlideUp_0.25s_ease-out]"
            >
                {/* Header (brand green) */}
                <div
                    className="flex items-start gap-3 px-5 sm:px-6 py-4 sm:py-5 text-white"
                    style={{ backgroundColor: BRAND_GREEN }}
                >
                    <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                        <HomeIcon size={18} aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 id={titleId} className="font-display text-[18px] sm:text-[20px] font-semibold leading-tight">
                            {contextTitle}
                        </h2>
                        {contextSubtitle ? (
                            <p className="text-[13px] text-white/80 mt-0.5 line-clamp-2">{contextSubtitle}</p>
                        ) : null}
                    </div>
                    <button
                        ref={closeBtnRef}
                        type="button"
                        onClick={handleClose}
                        aria-label="Close enquiry form"
                        className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center text-white flex-shrink-0"
                        data-testid="enquiry-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="bg-white px-5 sm:px-6 py-6">
                    {success ? (
                        <div className="py-6 text-center" data-testid="enquiry-success">
                            <div
                                className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center"
                                style={{ backgroundColor: 'rgba(27,67,50,0.08)' }}
                            >
                                <CheckCircle2 size={32} style={{ color: BRAND_GREEN }} aria-hidden="true" />
                            </div>
                            <div className="text-[18px] font-semibold text-[#18160E]">Thank you!</div>
                            <p className="text-[14px] text-[#48453F] mt-1">
                                We&apos;ll call you within 2 hours.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} noValidate>
                            {/* Hidden fields (still part of submission via state) */}
                            <input type="hidden" name="source" value={source} />
                            <input type="hidden" name="prefillProject" value={prefillProject || ''} />

                            <div className="mb-4">
                                <label
                                    htmlFor={`enquiry-name-${titleId}`}
                                    className="block text-[12px] font-semibold text-[#48453F] mb-1.5"
                                >
                                    Full Name <span className="text-[#E07B39]">*</span>
                                </label>
                                <input
                                    ref={firstFieldRef}
                                    id={`enquiry-name-${titleId}`}
                                    name="name"
                                    type="text"
                                    autoComplete="name"
                                    value={name}
                                    onChange={onFieldChange('name')}
                                    onBlur={onFieldBlur('name')}
                                    aria-invalid={errors.name ? 'true' : 'false'}
                                    aria-describedby={errors.name ? `enquiry-name-err-${titleId}` : undefined}
                                    placeholder="Your full name"
                                    className={`w-full h-11 px-3.5 rounded-lg border bg-white text-[14px] text-[#18160E] placeholder:text-[#A39F97] focus:outline-none focus:ring-2 focus:ring-[#1B4332]/30 ${
                                        errors.name ? 'border-[#C03A1E]' : 'border-[#D9D6CF]'
                                    }`}
                                    data-testid="enquiry-name"
                                />
                                {errors.name ? (
                                    <p
                                        id={`enquiry-name-err-${titleId}`}
                                        className="text-[12px] text-[#C03A1E] mt-1"
                                        role="alert"
                                    >
                                        {errors.name}
                                    </p>
                                ) : null}
                            </div>

                            <div className="mb-5">
                                <label
                                    htmlFor={`enquiry-phone-${titleId}`}
                                    className="block text-[12px] font-semibold text-[#48453F] mb-1.5"
                                >
                                    Phone Number <span className="text-[#E07B39]">*</span>
                                </label>
                                <div
                                    className={`flex items-stretch rounded-lg border bg-white overflow-hidden focus-within:ring-2 focus-within:ring-[#1B4332]/30 ${
                                        errors.phone ? 'border-[#C03A1E]' : 'border-[#D9D6CF]'
                                    }`}
                                >
                                    <span
                                        className="inline-flex items-center px-3 text-[13px] font-semibold text-[#48453F] bg-[#F4F3EF] border-r border-[#D9D6CF]"
                                        aria-hidden="true"
                                    >
                                        +91
                                    </span>
                                    <input
                                        id={`enquiry-phone-${titleId}`}
                                        name="phone"
                                        type="tel"
                                        inputMode="numeric"
                                        autoComplete="tel"
                                        maxLength={10}
                                        value={phone}
                                        onChange={onFieldChange('phone')}
                                        onBlur={onFieldBlur('phone')}
                                        aria-invalid={errors.phone ? 'true' : 'false'}
                                        aria-describedby={errors.phone ? `enquiry-phone-err-${titleId}` : undefined}
                                        placeholder="98XXXXXXXX"
                                        className="flex-1 h-11 px-3 text-[14px] text-[#18160E] placeholder:text-[#A39F97] focus:outline-none"
                                        data-testid="enquiry-phone"
                                    />
                                </div>
                                {errors.phone ? (
                                    <p
                                        id={`enquiry-phone-err-${titleId}`}
                                        className="text-[12px] text-[#C03A1E] mt-1"
                                        role="alert"
                                    >
                                        {errors.phone}
                                    </p>
                                ) : null}
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full h-12 rounded-lg text-white text-[14px] font-semibold tracking-wide transition-opacity disabled:opacity-60"
                                style={{ backgroundColor: BRAND_GREEN }}
                                data-testid="enquiry-submit"
                            >
                                {submitting ? 'Sending...' : 'Request Callback'}
                            </button>

                            <p className="text-[11px] text-[#87847E] text-center mt-3">
                                We respect your privacy. No spam, ever.
                            </p>
                        </form>
                    )}
                </div>
            </div>

            {/* Slide-up keyframe (scoped via style tag so we don't touch tailwind config) */}
            <style>{`
                @keyframes enquirySlideUp {
                    from { transform: translateY(24px); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
            `}</style>
        </div>
    );
}
